import {
  InvoicePayment,
  InvoicePaymentForm,
  InvoicePaymentTaxRegistration,
  PaymentTaxRegistrationState,
} from './_models'

export type PaymentTaxRegistrationBadge = {
  label: string
  className: string
  icon: string
  // پیام قابل نمایش زیر ردیف (خطای رسمی سازمان یا دلیل ثبت‌نشدن)
  message?: string
  // ردیف با «تلاش مجدد» دوباره به سامانه فرستاده می‌شود
  canRetry: boolean
  // پیش از تلاش مجدد باید کارپوشه بررسی شود (نتیجهٔ نامشخص)
  retryNeedsCheck: boolean
}

export const toPaymentTaxRegistration = (
  row: InvoicePayment
): InvoicePaymentTaxRegistration | undefined =>
  row.txrs == null
    ? undefined
    : {
        status: row.txrs,
        code: row.txrc,
        message: row.txrm,
        date: row.txrd,
        registrationId: row.txrid,
        amount: row.txra,
      }

// مبلغی که برای یک ردیف به کارپوشه می‌رود: سهم بدون مالیات بر ارزش افزوده (همان «سهم نقدی از پرداخت»
// که با نسبت مالیات به مبلغ نهایی صورتحساب حساب می‌شود). کارپوشه سهم مالیات را خودش می‌افزاید.
export const getPaymentRegisteredAmount = (payment: InvoicePaymentForm) => {
  if (payment.taxRegistration?.amount != null && payment.taxRegistration.amount > 0) {
    return Math.floor(payment.taxRegistration.amount)
  }
  const cash = Number(payment.cash)
  if (Number.isFinite(cash) && cash > 0) return Math.floor(cash)
  return Math.floor(Number(payment.price) || 0)
}

export const sumPaymentRegisteredAmounts = (payments: InvoicePaymentForm[]) =>
  payments.reduce((sum, payment) => sum + getPaymentRegisteredAmount(payment), 0)

// وضعیت ردیف پرداخت یک صورتحساب ارسال‌شده از دید سامانه مؤدیان
export const getPaymentTaxRegistrationBadge = (
  registration: InvoicePaymentTaxRegistration | undefined,
  locked: boolean
): PaymentTaxRegistrationBadge | undefined => {
  switch (registration?.status) {
    case PaymentTaxRegistrationState.Registered:
      return {
        label: 'ثبت‌شده در سامانه مؤدیان',
        className: 'badge-light-success',
        icon: 'la-check-double',
        canRetry: false,
        retryNeedsCheck: false,
      }
    case PaymentTaxRegistrationState.Queued:
      return {
        label: 'در صف ثبت در سامانه',
        className: 'badge-light-info',
        icon: 'la-clock',
        message: registration?.message,
        canRetry: false,
        retryNeedsCheck: false,
      }
    case PaymentTaxRegistrationState.Rejected:
      return {
        label: 'رد شده توسط سازمان',
        className: 'badge-light-danger',
        icon: 'la-times-circle',
        message: registration?.message,
        canRetry: true,
        retryNeedsCheck: false,
      }
    case PaymentTaxRegistrationState.Unknown:
      return {
        label: 'نتیجه ثبت نامشخص',
        className: 'badge-light-warning',
        icon: 'la-question-circle',
        message: registration?.message,
        canRetry: true,
        retryNeedsCheck: true,
      }
    case PaymentTaxRegistrationState.NotApplicable:
      return {
        label: 'در سامانه ثبت نمی‌شود',
        className: 'badge-light-secondary',
        icon: 'la-minus-circle',
        message: registration?.message,
        canRetry: false,
        retryNeedsCheck: false,
      }
    case PaymentTaxRegistrationState.Exhausted:
      return {
        label: 'ثبت در سامانه ناموفق',
        className: 'badge-light-warning',
        icon: 'la-redo-alt',
        message: registration?.message,
        canRetry: true,
        retryNeedsCheck: false,
      }
    default:
      return locked
        ? {
            label: 'ارسال‌شده همراه صورتحساب',
            className: 'badge-light-primary',
            icon: 'la-file-invoice',
            canRetry: false,
            retryNeedsCheck: false,
          }
        : undefined
  }
}

// همان فیلدهایی که سرور برای شناسایی ردیف می‌فرستد: روش، تاریخ، مبلغ، پایانه، شماره مرجع
export const paymentRegistrationKey = (payment: InvoicePaymentForm) =>
  [
    payment.type,
    (payment.date || '').trim(),
    Math.floor(Number(payment.price) || 0),
    (payment.terminalNumber || '').trim(),
    (payment.traceNo || payment.refereceNo || '').trim(),
  ].join('|')

// ردیف‌هایی که با ذخیره به سامانه مؤدیان فرستاده می‌شوند: ردیف جدید، یا ردیف بازِ قبلی که تغییر کرده است
export const getPaymentsToRegister = (
  payments: InvoicePaymentForm[],
  loadedKeys: Map<number, string>
) =>
  payments.filter((payment) => {
    if (payment.locked) return false
    if (!payment.id || payment.id <= 0) return true
    const loaded = loadedKeys.get(payment.id)
    return loaded === undefined || loaded !== paymentRegistrationKey(payment)
  })

export const isPaymentRowLocked = (payment: InvoicePaymentForm) => payment.locked === true
